import { useTranslation } from "react-i18next";
import { CalendarClock, TriangleAlert } from "lucide-react";

import type { ReassignmentPreview } from "../../../center";

interface ReassignmentWarningsProps {
    preview: ReassignmentPreview;
    /** Who the bookings would go to, as the picker names them. */
    therapistName: string;
}

/** `2026-09-14` as the reader writes a day. Built from its parts, so a
 * day-only string is never read as UTC midnight and shown as the day before. */
function formatDay(day: string): string {
    const [year, month, date] = day.split("-").map(Number);
    if (!year || !month || !date) return day;
    return new Date(year, month - 1, date).toLocaleDateString();
}

/**
 * What a handover would do to the bookings still to come, shown before it is
 * confirmed: how many move, which land on top of one the new therapist
 * already has, and which are on a Zoom or Meet link that stays in the
 * previous therapist's account.
 *
 * A status, not an error. Nothing here stops the handover — the confirm
 * button stays — and it says only when, never with whom: the centre sees
 * neither a session's title nor a way into it.
 */
export default function ReassignmentWarnings({ preview, therapistName }: ReassignmentWarningsProps) {
    const { t } = useTranslation();
    if (preview.future_appointments === 0) return null;

    const hasIssues = preview.clashes.length > 0 || preview.external_links.length > 0;
    const Icon = hasIssues ? TriangleAlert : CalendarClock;

    return (
        <div
            role="status"
            className={
                hasIssues
                    ? "mt-4 rounded-lg border border-warning-200 bg-status-warning-tint p-3 text-sm text-status-warning-on-tint dark:border-warning-800"
                    : "mt-4 rounded-lg bg-status-info-tint p-3 text-sm text-status-info-on-tint"
            }
        >
            <p className="flex items-start gap-2 font-medium">
                <Icon aria-hidden="true" className="mt-0.5 h-4 w-4 shrink-0" />
                <span>
                    {t("center.patients.preview.moving", {
                        count: preview.future_appointments,
                        therapist: therapistName,
                    })}
                </span>
            </p>
            {hasIssues && (
                <ul className="mt-2 list-disc space-y-1 pl-10">
                    {preview.clashes.map((clash) => (
                        <li key={`clash:${clash.appointment_id}`}>
                            {t(
                                clash.same_slot
                                    ? "center.patients.preview.sameSlot"
                                    : "center.patients.preview.clash",
                                { date: formatDay(clash.date), hour: clash.hour }
                            )}
                        </li>
                    ))}
                    {preview.external_links.map((link) => (
                        <li key={`link:${link.appointment_id}`}>
                            {t("center.patients.preview.externalLink", {
                                date: formatDay(link.date),
                                hour: link.hour,
                                platform: t(`center.patients.preview.platforms.${link.platform}`, {
                                    defaultValue: link.platform,
                                }),
                            })}
                        </li>
                    ))}
                </ul>
            )}
        </div>
    );
}
